import { useState, useMemo } from 'react';
import Icon from '@mdi/react';
import { mdiAccountTie, mdiMagnify, mdiChevronLeft } from '@mdi/js';
import { toast } from 'react-toastify';
import BaseModal from '../BaseModal';
import { classApi } from '../../api/classes';
import './InstructorDetailModal.css';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function initials(displayName) {
    return (displayName || '')
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map(w => w[0].toUpperCase())
        .join('');
}

/**
 * InstructorDetailModal
 *
 * Props:
 *   admin          { accountId, displayName, isOwner }
 *   allClasses     flat array of all class definitions (with primaryInstructors[])
 *   terms          array of term objects { id, name }
 *   shortCourses   flat array of short course objects (with primaryInstructors[])
 *   onClose        ()  → void
 *   onChanged      (classId, primaryInstructors[]) → void  — called after save/remove (weekly classes)
 *   onScChanged    (shortCourseId, primaryInstructors[]) → void  — called after sc save/remove
 */
const InstructorDetailModal = ({ admin, allClasses, terms, shortCourses = [], onClose, onChanged, onScChanged }) => {
    const [step, setStep] = useState('detail'); // 'detail' | 'assign'
    const [filter, setFilter] = useState('');
    const [selected, setSelected] = useState(null); // Set of classIds — initialised on assign step open
    const [selectedSc, setSelectedSc] = useState(null); // Set of shortCourseIds
    const [isSaving, setIsSaving] = useState(false);
    const [removingClassId, setRemovingClassId] = useState(null);

    const assignedClasses = useMemo(
        () => allClasses.filter(c => (c.primaryInstructors || []).includes(admin.accountId)),
        [allClasses, admin.accountId]
    );

    const assignedShortCourses = useMemo(
        () => shortCourses.filter(c => (c.primaryInstructors || []).includes(admin.accountId)),
        [shortCourses, admin.accountId]
    );

    // Group all classes by term for the assign step
    const classesGroupedByTerm = useMemo(() => {
        const lc = filter.toLowerCase();
        return terms.map(term => {
            const classes = allClasses.filter(c =>
                c.termId === term.id &&
                (filter === '' || c.name?.toLowerCase().includes(lc))
            );
            return { term, classes };
        }).filter(g => g.classes.length > 0);
    }, [allClasses, terms, filter]);

    const openAssign = () => {
        setSelected(new Set(assignedClasses.map(c => c.id)));
        setSelectedSc(new Set(assignedShortCourses.map(c => c.id)));
        setFilter('');
        setStep('assign');
    };

    const toggleClass = (classId) => {
        setSelected(prev => {
            const next = new Set(prev);
            if (next.has(classId)) next.delete(classId);
            else next.add(classId);
            return next;
        });
    };

    const handleRemove = async (cls) => {
        setRemovingClassId(cls.id);
        try {
            const newInstructors = (cls.primaryInstructors || []).filter(id => id !== admin.accountId);
            const res = await classApi.updateClass(cls.id, { primaryInstructors: newInstructors });
            if (res.ok) {
                toast.success(`Removed from ${cls.name}`);
                onChanged(cls.id, newInstructors);
            } else {
                toast.error(res.data?.message || 'Failed to remove');
            }
        } catch {
            toast.error('Failed to remove');
        } finally {
            setRemovingClassId(null);
        }
    };

    const handleRemoveShortCourse = async (sc) => {
        setRemovingClassId(sc.id);
        try {
            const newInstructors = (sc.primaryInstructors || []).filter(id => id !== admin.accountId);
            const res = await classApi.updateShortCourseInstructors(sc.id, newInstructors);
            if (res.ok) {
                toast.success(`Removed from ${sc.name}`);
                if (onScChanged) onScChanged(sc.id, newInstructors);
            } else {
                toast.error(res.data?.message || 'Failed to remove');
            }
        } catch {
            toast.error('Failed to remove');
        } finally {
            setRemovingClassId(null);
        }
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            const currentIds = new Set(assignedClasses.map(c => c.id));
            const currentScIds = new Set(assignedShortCourses.map(c => c.id));

            // Weekly class changes
            const toAdd = [...selected].filter(id => !currentIds.has(id));
            const toRemove = [...currentIds].filter(id => !selected.has(id));

            const classOps = [
                ...toAdd.map(id => {
                    const cls = allClasses.find(c => c.id === id);
                    const newInstructors = [...new Set([...(cls.primaryInstructors || []), admin.accountId])];
                    return classApi.updateClass(id, { primaryInstructors: newInstructors })
                        .then(res => ({ id, res, newInstructors, type: 'class' }));
                }),
                ...toRemove.map(id => {
                    const cls = allClasses.find(c => c.id === id);
                    const newInstructors = (cls.primaryInstructors || []).filter(aid => aid !== admin.accountId);
                    return classApi.updateClass(id, { primaryInstructors: newInstructors })
                        .then(res => ({ id, res, newInstructors, type: 'class' }));
                }),
            ];

            // Short course changes
            const scToAdd = [...selectedSc].filter(id => !currentScIds.has(id));
            const scToRemove = [...currentScIds].filter(id => !selectedSc.has(id));

            const scOps = [
                ...scToAdd.map(id => {
                    const sc = shortCourses.find(c => c.id === id);
                    const newInstructors = [...new Set([...(sc.primaryInstructors || []), admin.accountId])];
                    return classApi.updateShortCourseInstructors(id, newInstructors)
                        .then(res => ({ id, res, newInstructors, type: 'shortCourse' }));
                }),
                ...scToRemove.map(id => {
                    const sc = shortCourses.find(c => c.id === id);
                    const newInstructors = (sc.primaryInstructors || []).filter(aid => aid !== admin.accountId);
                    return classApi.updateShortCourseInstructors(id, newInstructors)
                        .then(res => ({ id, res, newInstructors, type: 'shortCourse' }));
                }),
            ];

            // Run both batches; short course failures don't block class saves
            const [classResults, scResults] = await Promise.all([
                Promise.all(classOps),
                Promise.all(scOps),
            ]);

            let anyError = false;
            for (const { id, res, newInstructors } of classResults) {
                if (res.ok) { onChanged(id, newInstructors); } else { anyError = true; }
            }
            for (const { id, res, newInstructors } of scResults) {
                if (res.ok) { if (onScChanged) onScChanged(id, newInstructors); } else { anyError = true; }
            }

            if (anyError) {
                toast.error('Some changes could not be saved');
            } else if (classOps.length + scOps.length > 0) {
                toast.success('Assignments updated');
            }

            setStep('detail');
        } catch {
            toast.error('Failed to save assignments');
        } finally {
            setIsSaving(false);
        }
    };

    if (step === 'assign') {
        return (
            <BaseModal
                isOpen
                onClose={onClose}
                title={admin.displayName}
                icon={mdiAccountTie}
                maxWidth={540}
                footer={
                    <>
                        <button className="base-modal-cancel-btn" onClick={() => setStep('detail')}>
                            Back
                        </button>
                        <button className="base-modal-save-btn" onClick={handleSave} disabled={isSaving}>
                            {isSaving ? 'Saving...' : 'Save'}
                        </button>
                    </>
                }
            >
                <div className="instructor-detail-modal">
                    <div className="instructor-detail-modal-back-hint">
                        <Icon path={mdiChevronLeft} size={0.8} color="#888" />
                        <span>Select classes to assign</span>
                    </div>

                    <div className="instructor-detail-modal-filter-row">
                        <Icon path={mdiMagnify} size={0.9} color="#888" />
                        <input
                            type="text"
                            className="instructor-detail-modal-filter"
                            placeholder="Filter classes..."
                            value={filter}
                            onChange={e => setFilter(e.target.value)}
                        />
                    </div>

                    {classesGroupedByTerm.length === 0 ? (
                        <p className="instructor-detail-modal-empty">No classes found</p>
                    ) : (
                        classesGroupedByTerm.map(({ term, classes }) => (
                            <div key={term.id} className="instructor-detail-modal-term-group">
                                <div className="instructor-detail-modal-term-label">{term.name}</div>
                                {classes.map(cls => (
                                    <label key={cls.id} className="instructor-detail-modal-assign-row">
                                        <input
                                            type="checkbox"
                                            checked={selected.has(cls.id)}
                                            onChange={() => toggleClass(cls.id)}
                                        />
                                        <div className="instructor-detail-modal-assign-info">
                                            <span className="instructor-detail-modal-assign-name">{cls.name}</span>
                                            <span className="instructor-detail-modal-assign-meta">
                                                {DAYS[cls.dayOfWeek]} · {cls.startTime}–{cls.endTime}
                                                {cls.style ? ` · ${cls.style}` : ''}
                                            </span>
                                        </div>
                                    </label>
                                ))}
                            </div>
                        ))
                    )}

                    {shortCourses.length > 0 && (
                        <div className="instructor-detail-modal-term-group">
                            <div className="instructor-detail-modal-term-label">Short Courses</div>
                            {shortCourses.map(sc => (
                                <label key={sc.id} className="instructor-detail-modal-assign-row">
                                    <input
                                        type="checkbox"
                                        checked={selectedSc.has(sc.id)}
                                        onChange={() => setSelectedSc(prev => {
                                            const next = new Set(prev);
                                            if (next.has(sc.id)) next.delete(sc.id);
                                            else next.add(sc.id);
                                            return next;
                                        })}
                                    />
                                    <div className="instructor-detail-modal-assign-info">
                                        <span className="instructor-detail-modal-assign-name">{sc.name}</span>
                                        <span className="instructor-detail-modal-assign-meta">
                                            {DAYS[sc.dayOfWeek]} · {sc.startTime}–{sc.endTime}
                                            {sc.style ? ` · ${sc.style}` : ''}
                                        </span>
                                    </div>
                                </label>
                            ))}
                        </div>
                    )}
                </div>
            </BaseModal>
        );
    }

    // Detail step
    return (
        <BaseModal
            isOpen
            onClose={onClose}
            title={admin.displayName}
            icon={mdiAccountTie}
            maxWidth={500}
            footer={
                <>
                    <button className="base-modal-cancel-btn" onClick={onClose}>Close</button>
                    <button className="base-modal-save-btn" onClick={openAssign}>
                        Assign to Class
                    </button>
                </>
            }
        >
            <div className="instructor-detail-modal">
                <div className="instructor-detail-modal-profile">
                    <div className="instructor-detail-modal-avatar">
                        {initials(admin.displayName)}
                    </div>
                    <div>
                        <div className="instructor-detail-modal-name">{admin.displayName}</div>
                        <span className={`instructor-detail-modal-role-badge${admin.isOwner ? ' owner' : ''}`}>
                            {admin.isOwner ? 'Owner' : 'Team Member'}
                        </span>
                    </div>
                </div>

                <div className="instructor-detail-modal-section-label">Assigned Classes</div>

                {assignedClasses.length === 0 ? (
                    <p className="instructor-detail-modal-empty">No classes assigned yet</p>
                ) : (
                    assignedClasses.map(cls => (
                        <div key={cls.id} className="instructor-detail-modal-class-row">
                            <div className="instructor-detail-modal-class-info">
                                <span className="instructor-detail-modal-class-name">{cls.name}</span>
                                <span className="instructor-detail-modal-class-meta">
                                    {DAYS[cls.dayOfWeek]} · {cls.startTime}–{cls.endTime}
                                    {cls.style ? ` · ${cls.style}` : ''}
                                </span>
                            </div>
                            <button
                                className="instructor-detail-modal-remove-btn"
                                onClick={() => handleRemove(cls)}
                                disabled={removingClassId === cls.id}
                            >
                                {removingClassId === cls.id ? '...' : 'Remove'}
                            </button>
                        </div>
                    ))
                )}

                {assignedShortCourses.length > 0 && (
                    <>
                        <div className="instructor-detail-modal-section-label" style={{ marginTop: 16 }}>Assigned Short Courses</div>
                        {assignedShortCourses.map(sc => (
                            <div key={sc.id} className="instructor-detail-modal-class-row">
                                <div className="instructor-detail-modal-class-info">
                                    <span className="instructor-detail-modal-class-name">{sc.name}</span>
                                    <span className="instructor-detail-modal-class-meta">
                                        {DAYS[sc.dayOfWeek]} · {sc.startTime}–{sc.endTime}
                                        {sc.style ? ` · ${sc.style}` : ''}
                                    </span>
                                </div>
                                <button
                                    className="instructor-detail-modal-remove-btn"
                                    onClick={() => handleRemoveShortCourse(sc)}
                                    disabled={removingClassId === sc.id}
                                >
                                    {removingClassId === sc.id ? '...' : 'Remove'}
                                </button>
                            </div>
                        ))}
                    </>
                )}
            </div>
        </BaseModal>
    );
};

export default InstructorDetailModal;
